import * as React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ProcessSteps, type ProcessStep } from "@/components/sections/ProcessSteps";
import { FeatureRow, type FeatureRowItem } from "@/components/sections/FeatureRow";
import { MediaImage } from "@/components/MediaImage";

/**
 * Maps section image alt text to a relevant topical photograph from the
 * generated library. Used to replace the legacy generic SVG icons with
 * professional, topic-relevant imagery without editing every content file.
 */
const TOPIC_IMAGE_LIBRARY: { test: RegExp; src: string }[] = [
  {
    test: /(responsive|mobile|device|tablet|phone)/i,
    src: "/images/generated/topic-responsive-design.jpg",
  },
  {
    test: /(planning|strategy|discovery|wireframe|roadmap|consult)/i,
  src: "/images/brand/seo-strategy-1200.webp",
  },
  { test: /(wordpress|cms)/i, src: "/images/generated/topic-wordpress.jpg" },
  {
    test: /(shopify|ecommerce|e-commerce|store|product)/i,
    src: "/images/generated/topic-shopify-ecommerce.jpg",
  },
  {
    test: /(host|server|cloud|uptime|infrastructure)/i,
    src: "/images/generated/topic-hosting-servers.jpg",
  },
  {
    test: /(security|update|backup|patch|maintenance)/i,
    src: "/images/generated/topic-security-updates.jpg",
  },
  {
    test: /(manage|support|monitor|ongoing)/i,
    src: "/images/generated/topic-website-management.jpg",
  },
  { test: /(audit|technical|crawl|index)/i, src: "/images/generated/topic-seo-audit.jpg" },
  {
    test: /(analytic|report|measure|tracking|data|insight)/i,
    src: "/images/generated/topic-seo-analytics.jpg",
  },
  {
    test: /(content|copy|writing|blog|outreach|link)/i,
    src: "/images/generated/topic-content.jpg",
  },
  {
    test: /(performance|speed|core web vitals|optimis)/i,
    src: "/images/generated/topic-performance.jpg",
  },
  {
    test: /(local|area|barnsley|wakefield|sheffield|doncaster|rotherham|huddersfield|yorkshire)/i,
    src: "/images/generated/topic-local-area.jpg",
  },
  {
    test: /(web design|design|build|launch|ux|ui|brand|credibility|advantage|cost)/i,
    src: "/images/generated/topic-web-design-planning.jpg",
  },
  {
    test: /(seo|organic|search|ranking|keyword|visibility|mission|vision|value)/i,
    src: "/images/generated/topic-seo-analytics.jpg",
  },
];

const ICON_LIKE_HOSTS = /\/images\/(icon-|about-bg|sub-)/i;

function resolveTopicImage(
  originalSrc: string | undefined,
  alt: string | undefined,
): string | undefined {
  if (!originalSrc) return originalSrc;
  // Only swap when the source looks like one of the legacy generic icons.
  if (!ICON_LIKE_HOSTS.test(originalSrc)) return originalSrc;
  const altText = (alt ?? "").trim();
  for (const entry of TOPIC_IMAGE_LIBRARY) {
    if (entry.test.test(altText)) return entry.src;
  }
  return "/images/generated/topic-web-design-planning.jpg";
}

type MarkdownSegment =
  | { type: "markdown"; content: string }
  | { type: "faq"; intro: string; items: FaqItem[] }
  | { type: "process"; steps: ProcessStep[] }
  | { type: "sectionHeader"; eyebrow?: string; heading: string; intro?: string }
  | { type: "features"; items: FeatureRowItem[] };

type FaqItem = {
  question: string;
  answer: string;
};

/**
 * Renders scraped markdown body content with site-themed prose styling.
 * Used by dynamic content routes (locations, blog posts, static pages).
 */
export function MarkdownBody({
  children,
  imageFallback,
  flatHeadings = false,
}: {
  children: string;
  imageFallback?: string;
  flatHeadings?: boolean;
}) {
  const segments = parseMarkdownSegments(children);
  const faqItems = segments.flatMap((segment) => segment.type === "faq" ? segment.items : []);

  return (
    <div className="markdown-body demo-markdown prose prose-invert max-w-none prose-headings:font-display prose-headings:font-light prose-headings:tracking-normal prose-p:leading-relaxed prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-ul:pl-0 prose-ol:pl-0 prose-li:my-1 prose-li:pl-0 prose-li:leading-relaxed prose-li:marker:text-primary prose-img:mx-auto prose-img:max-w-full prose-img:rounded-[10px] [&_li]:ml-0 [&_ol]:list-inside [&_ul]:list-inside">
      {faqItems.length > 0 && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map(({ question, answer }) => ({
          "@type": "Question", name: question,
          acceptedAnswer: { "@type": "Answer", text: answer.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1").replace(/[*_`]/g, "").replace(/\s+/g, " ").trim() },
        })),
      }).replace(/</g, "\\u003c") }} />}
      {segments.map((segment, index) =>
        segment.type === "faq" ? (
          <FaqDropdownSection
            key={index}
            segment={segment}
            index={index}
            imageFallback={imageFallback}
          />
        ) : segment.type === "process" ? (
          <ProcessSteps key={index} steps={segment.steps} />
        ) : segment.type === "sectionHeader" ? (
          flatHeadings ? (
            <FlatSectionHeader key={index} segment={segment} />
          ) : (
            <SectionHeader key={index} segment={segment} />
          )
        ) : segment.type === "features" ? (
          <FeatureRow
            key={index}
            items={segment.items.map((item) => ({
              ...item,
              image: resolveTopicImage(item.image, item.imageAlt) ?? item.image,
            }))}
            imageFallback={imageFallback}
          />
        ) : (
          <MarkdownRenderer key={index} imageFallback={imageFallback}>
            {segment.content}
          </MarkdownRenderer>
        ),
      )}
    </div>
  );
}

function SectionHeader({
  segment,
}: {
  segment: Extract<MarkdownSegment, { type: "sectionHeader" }>;
}) {
  return (
    <div className="not-prose row g-5 align-items-end mt-10 mb-6">
      <div className="col-lg-7">
        {segment.eyebrow && <p className="demo-section-label">{segment.eyebrow}</p>}
        <h2 className="site-heading-lg mt-4 font-display font-light">{segment.heading}</h2>
      </div>
      {segment.intro && (
        <div className="col-lg-5">
          <div className="site-copy text-muted-foreground [&_a]:text-primary [&_a:hover]:underline [&_p]:m-0">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{segment.intro}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}

function FlatSectionHeader({
  segment,
}: {
  segment: Extract<MarkdownSegment, { type: "sectionHeader" }>;
}) {
  return (
    <div className="mt-12 mb-4">
      {segment.eyebrow && <p className="demo-section-label not-prose">{segment.eyebrow}</p>}
      <h2 className="site-heading-lg font-display font-light">{segment.heading}</h2>
      {segment.intro && (
        <div className="mt-4 [&_a]:text-primary [&_a:hover]:underline">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{segment.intro}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}

function MarkdownRenderer({
  children,
  imageFallback,
}: {
  children: string;
  imageFallback?: string;
}) {
  if (!children.trim()) return null;

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => {
          const childArray = React.Children.toArray(children);
          if (childArray.length > 0 && childArray.every(isImageLikeChild)) {
            const count = childArray.length;
            const colClass =
              count >= 3 ? "col-12 col-sm-6 col-lg-4" : count === 2 ? "col-12 col-md-6" : "col-12";
            return (
              <div className="demo-image-row row g-4 align-items-center">
                {childArray.map((child, index) => (
                  <div className={colClass} key={index}>
                    <figure className="demo-image-frame demo-image-frame-markdown">{child}</figure>
                  </div>
                ))}
              </div>
            );
          }

          return <p>{children}</p>;
        },
        a: ({ href, children, ...props }) => {
          const normalizedHref = href?.includes("#authorModal") ? "/about" : href;
          const isContactCta = normalizedHref?.includes("/contact");
          if (isContactCta) {
            return (
              <a
                href={normalizedHref}
                {...props}
                className="demo-button-primary not-prose inline-flex items-center gap-3 px-7 py-4 text-sm font-semibold no-underline transition-transform hover:-translate-y-0.5"
              >
                {children}
                <span aria-hidden className="inline-block">
                  →
                </span>
              </a>
            );
          }
          return (
            <a href={normalizedHref} {...props}>
              {children}
            </a>
          );
        },
        h3: ({ children, ...props }) => {
          const text = React.Children.toArray(children)
            .map((c) => (typeof c === "string" ? c : ""))
            .join("")
            .trim();
          // Treat short, lowercase h3 lines as section-label chips
          // (e.g. "about", "my approach", "faqs", "client benefits")
          const isLabel =
            text.length > 0 &&
            text.length <= 40 &&
            text === text.toLowerCase() &&
            !/[.?!]/.test(text);
          if (isLabel) {
            return <p className="demo-section-label not-prose mt-10">{text}</p>;
          }
          return <h3 {...props}>{children}</h3>;
        },
        h2: ({ children, ...props }) => (
          <h2 {...props} className="site-heading-lg font-display font-light">
            {children}
          </h2>
        ),
        img: ({ alt, title, className, ...props }) => {
          const imageText = alt?.trim() || "Phil Carr SEO and web design image";
          const resolvedSrc = resolveTopicImage(props.src as string | undefined, alt);
          // react-markdown attaches `node` to component props; strip it so we
          // don't render a `node="[object Object]"` attribute on <img>.
          const { node: _node, src: _src, ...safeProps } = props as Record<string, unknown>;
          return (
            <MediaImage
              {...(safeProps as React.ImgHTMLAttributes<HTMLImageElement>)}
              src={resolvedSrc ?? ""}
              alt={imageText}
              title={title || imageText}
              imgClassName={className}
              fallbackSrc={imageFallback}
            />
          );
        },
      }}
    >
      {children}
    </ReactMarkdown>
  );
}

function FaqDropdownSection({
  segment,
  index,
  imageFallback,
}: {
  segment: Extract<MarkdownSegment, { type: "faq" }>;
  index: number;
  imageFallback?: string;
}) {
  // Parse the intro lines into a sub-heading (h3) + main heading (h2) so we
  // can render them with the homepage `.demo-section-heading` styling.
  const introLines = segment.intro
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const subHeading = introLines.find((l) => /^###\s+/.test(l))?.replace(/^###\s+/, "") ?? "";
  const mainHeading =
    introLines.find((l) => /^##\s+/.test(l) && !/^###\s+/.test(l))?.replace(/^##\s+/, "") ?? "";
  return (
    <section className="not-prose demo-faq-section page-faq-section border-y border-border">
      <div className="page-faq-inner">
        {(subHeading || mainHeading) && (
          <div className="demo-section-heading section-title">
            {subHeading && <p className="demo-section-label">{subHeading}</p>}
            {mainHeading && (
              <h2 className="site-heading-md mt-4 font-display font-light">{mainHeading}</h2>
            )}
          </div>
        )}
        <Accordion type="single" collapsible className="demo-accordion faq-accordion w-full">
          {segment.items.map((item, itemIndex) => (
            <AccordionItem
              key={`${index}-${itemIndex}`}
              value={`faq-${index}-${itemIndex}`}
              className="demo-accordion-item"
            >
              <AccordionTrigger className="demo-accordion-trigger group relative flex w-full text-left font-display hover:no-underline [&>svg.lucide-chevron-down]:hidden">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="demo-accordion-content text-base leading-relaxed">
                <MarkdownRenderer imageFallback={imageFallback}>{item.answer}</MarkdownRenderer>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function extractLeadImage(body: string): {
  image?: { src: string; alt: string };
  body: string;
} {
  const match = body.match(/^!\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)\s*$/m);
  if (!match) return { body };
  const [, alt, src] = match;
  return {
    image: { src, alt: alt || "Page hero image" },
    body: body.replace(match[0], "").replace(/\n{3,}/g, "\n\n"),
  };
}

export function prepareMarkdownBodyForPage(body: string): {
  body: string;
  leadImage?: { src: string; alt: string };
} {
  const { image, body: bodyWithoutLeadImage } = extractLeadImage(body);
  return { body: bodyWithoutLeadImage, leadImage: image };
}

function parseMarkdownSegments(markdown: string): MarkdownSegment[] {
  const lines = markdown.split("\n");
  const segments: MarkdownSegment[] = [];
  let buffer: string[] = [];
  let index = 0;

  const flushBuffer = () => {
    const text = buffer.join("\n");
    if (text.trim()) {
      // Split buffer into chunks at section-header boundaries
      // (an optional `### eyebrow` followed by a `## heading`).
      const chunks = splitIntoSectionChunks(text);
      for (const chunk of chunks) {
        if (chunk.header) {
          segments.push({
            type: "sectionHeader",
            eyebrow: chunk.header.eyebrow,
            heading: chunk.header.heading,
            intro: chunk.header.intro,
          });
        }
        const body = chunk.body;
        if (body.trim()) {
          const { remaining, features } = extractFeatureTriplets(body);
          if (remaining.trim()) segments.push({ type: "markdown", content: remaining });
          if (features.length) segments.push({ type: "features", items: features });
        }
      }
    }
    buffer = [];
  };

  while (index < lines.length) {
    const line = lines[index];

    // Process steps: sequence of `## 0X` followed by `### Title` and a paragraph.
    if (/^##\s+0[1-9]\s*$/.test(line.trim())) {
      const steps: ProcessStep[] = [];
      while (index < lines.length && /^##\s+0[1-9]\s*$/.test(lines[index]?.trim() ?? "")) {
        const n = lines[index].trim().replace(/^##\s+/, "");
        index += 1;
        while (index < lines.length && lines[index].trim() === "") index += 1;
        const headingLine = lines[index] ?? "";
        if (!/^###\s+/.test(headingLine)) break;
        const title = headingLine.replace(/^###\s+/, "").trim();
        index += 1;
        const bodyLines: string[] = [];
        while (
          index < lines.length &&
          !/^##\s+0[1-9]\s*$/.test(lines[index].trim()) &&
          !/^##\s+/.test(lines[index]) &&
          !/^###\s+/.test(lines[index])
        ) {
          bodyLines.push(lines[index]);
          index += 1;
        }
        steps.push({ n, title, body: bodyLines.join("\n").trim() });
      }
      if (steps.length) {
        flushBuffer();
        segments.push({ type: "process", steps });
        continue;
      }
    }

    if (isFaqMarker(line)) {
      const markerLevel = getHeadingLevel(line);
      // A legacy page uses `### faqs` followed by a `## ...` heading, while
      // scraped pages such as FlashRouters often use `## Frequently asked questions`
      // directly followed by plain text question/answer pairs.
      let lookahead = index + 1;
      while (lookahead < lines.length && lines[lookahead].trim() === "") lookahead += 1;
      const sectionHeading = lines[lookahead] ?? "";
      const usesSeparateSectionHeading = markerLevel === 3;
      if (usesSeparateSectionHeading && !/^##\s+/.test(sectionHeading)) {
        buffer.push(line);
        index += 1;
        continue;
      }

      flushBuffer();

      const intro = usesSeparateSectionHeading ? [line, "", sectionHeading].join("\n") : line;
      index = usesSeparateSectionHeading ? lookahead + 1 : index + 1;
      const items: FaqItem[] = [];

      while (index < lines.length) {
        // Stop the FAQ block when we hit a new top-level marker (### ...) — e.g. a CTA section.
        if (/^###\s+/.test(lines[index]) && !isFaqMarker(lines[index])) break;
        if (/^#{1,6}\s+/.test(lines[index]) && !/^##\s+/.test(lines[index])) break;
        if (!/^##\s+/.test(lines[index]) && !isPlainFaqQuestion(lines[index])) {
          index += 1;
          continue;
        }

        const questionStart = index;
        const isHeadingQuestion = /^##\s+/.test(lines[index]);
        const question = isHeadingQuestion
          ? lines[index].replace(/^##\s+/, "").trim()
          : lines[index].trim();
        // A following section heading belongs to the article, not the FAQ.
        if (!question.endsWith("?")) break;
        const answerLines: string[] = [];
        index += 1;

        while (
          index < lines.length &&
          !/^#{1,6}\s+/.test(lines[index]) &&
          !(answerLines.some((l) => l.trim()) && isPlainFaqQuestion(lines[index])) &&
          !/^###\s+/.test(lines[index]) &&
          !isFaqMarker(lines[index])
        ) {
          answerLines.push(lines[index]);
          index += 1;
        }

        const answer = answerLines.join("\n").trim();
        // If the answer contains a standalone CTA link (e.g. "[Contact](/contact)"),
        // this is a closing CTA section, not a FAQ item — terminate the FAQ block
        // and let the rest fall through as normal markdown / sectionHeader.
        const isCtaSection = answerLines.some((l) => /^\s*\[[^\]]+\]\([^)]+\)\s*$/.test(l));
        if (isCtaSection) {
          index = questionStart;
          break;
        }
        if (question && answer) items.push({ question, answer });

        if (isFaqMarker(lines[index]) || /^###\s+/.test(lines[index] ?? "")) break;
      }

      if (items.length) segments.push({ type: "faq", intro, items });
      continue;
    }

    buffer.push(line);
    index += 1;
  }

  flushBuffer();
  return segments.length ? segments : [{ type: "markdown", content: markdown }];
}

/**
 * Extracts groups of "topic blocks" written in the scraped markdown as
 *   ![alt](src)
 *   ### Title
 *   paragraph...
 * When 2+ consecutive blocks are found, they're rendered as a themed
 * FeatureRow. Remaining content is returned as plain markdown.
 */
function extractFeatureTriplets(text: string): { remaining: string; features: FeatureRowItem[] } {
  const lines = text.split("\n");
  const out: string[] = [];
  const features: FeatureRowItem[] = [];
  let i = 0;

  const tryParseTriplet = (start: number): { item: FeatureRowItem; next: number } | null => {
    let cursor = start;
    while (cursor < lines.length && lines[cursor].trim() === "") cursor += 1;
    const imgMatch = lines[cursor]?.match(/^!\[([^\]]*)\]\(([^)]+)\)\s*$/);
    if (!imgMatch) return null;
    const [, alt, src] = imgMatch;
    cursor += 1;
    while (cursor < lines.length && lines[cursor].trim() === "") cursor += 1;
    const headingMatch = lines[cursor]?.match(/^###\s+(.+)$/);
    if (!headingMatch) return null;
    const title = headingMatch[1].trim();
    cursor += 1;
    while (cursor < lines.length && lines[cursor].trim() === "") cursor += 1;
    const bodyLines: string[] = [];
    while (
      cursor < lines.length &&
      !/^#{1,6}\s+/.test(lines[cursor]) &&
      !/^!\[[^\]]*\]\([^)]+\)\s*$/.test(lines[cursor])
    ) {
      bodyLines.push(lines[cursor]);
      cursor += 1;
    }
    const body = bodyLines.join("\n").trim();
    if (!body) return null;
    return { item: { image: src, imageAlt: alt, title, body }, next: cursor };
  };

  while (i < lines.length) {
    const group: FeatureRowItem[] = [];
    let cursor = i;
    while (true) {
      const result = tryParseTriplet(cursor);
      if (!result) break;
      group.push(result.item);
      cursor = result.next;
    }
    if (group.length >= 2) {
      features.push(...group);
      i = cursor;
      continue;
    }
    out.push(lines[i]);
    i += 1;
  }

  return { remaining: out.join("\n"), features };
}

function isFaqMarker(line = "") {
  return (
    /^#{2,3}\s+.*faqs?\s*$/i.test(line.trim()) ||
    /^#{2,3}\s+frequently asked questions\s*$/i.test(line.trim())
  );
}

function getHeadingLevel(line = "") {
  return line.match(/^(#{1,6})\s+/)?.[1]?.length ?? 0;
}

function isPlainFaqQuestion(line = "") {
  const text = line.trim();
  return text.length > 8 && text.length <= 140 && text.endsWith("?") && !/^[-*>`![\]]/.test(text);
}

/**
 * Splits a markdown buffer into chunks separated by section-header boundaries.
 * Each chunk has an optional header (eyebrow + heading + intro paragraph) and
 * the remaining body content that follows it (until the next header).
 */
function splitIntoSectionChunks(text: string): Array<{
  header?: { eyebrow?: string; heading: string; intro?: string };
  body: string;
}> {
  const lines = text.split("\n");
  const chunks: Array<{
    header?: { eyebrow?: string; heading: string; intro?: string };
    body: string;
  }> = [];

  let current: {
    header?: { eyebrow?: string; heading: string; intro?: string };
    bodyLines: string[];
  } = {
    bodyLines: [],
  };
  const push = () => {
    chunks.push({ header: current.header, body: current.bodyLines.join("\n") });
    current = { bodyLines: [] };
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // Look for `### eyebrow\n\n## heading` or lone `## heading`.
    const isH3 = /^###\s+.+/.test(line) && !isFaqMarker(line);
    const isH2 = /^##\s+/.test(line) && !/^##\s+0[1-9]\s*$/.test(line.trim());

    let eyebrow: string | undefined;
    let headingIdx = -1;

    if (isH3) {
      // Peek ahead past blank lines for a ## heading.
      let look = i + 1;
      while (look < lines.length && lines[look].trim() === "") look += 1;
      if (
        look < lines.length &&
        /^##\s+/.test(lines[look]) &&
        !/^##\s+0[1-9]\s*$/.test(lines[look].trim())
      ) {
        eyebrow = line.replace(/^###\s+/, "").trim();
        headingIdx = look;
      }
    } else if (isH2) {
      headingIdx = i;
    }

    if (headingIdx >= 0) {
      const heading = lines[headingIdx].replace(/^##\s+/, "").trim();
      // Find the first paragraph following the heading to use as intro copy.
      let cursor = headingIdx + 1;
      while (cursor < lines.length && lines[cursor].trim() === "") cursor += 1;
      const introLines: string[] = [];
      while (
        cursor < lines.length &&
        lines[cursor].trim() !== "" &&
        !/^#{1,6}\s+/.test(lines[cursor]) &&
        !/^[-*]\s+/.test(lines[cursor]) &&
        !/^!\[[^\]]*\]\([^)]+\)/.test(lines[cursor]) &&
        !/^\[[^\]]+\]\([^)]+\)\s*$/.test(lines[cursor])
      ) {
        introLines.push(lines[cursor]);
        cursor += 1;
      }
      const intro = introLines.join(" ").trim() || undefined;

      // Close the previous chunk's body and start a new one with this header.
      push();
      current.header = { eyebrow, heading, intro };
      i = cursor;
      continue;
    }

    current.bodyLines.push(line);
    i += 1;
  }
  push();

  // Drop empty leading chunk.
  return chunks.filter((c) => c.header || c.body.trim());
}

function isImageLikeChild(child: React.ReactNode) {
  if (!React.isValidElement<{ children?: React.ReactNode }>(child)) return false;
  if (child.type === "img") return true;
  if (child.type !== "a") return false;
  const linkedChildren = React.Children.toArray(child.props.children);
  return linkedChildren.length > 0 && linkedChildren.every(isImageLikeChild);
}
